import { Images } from 'src/image';
import type { FC } from 'react';
import { Icon } from 'src/common/components/icon';
import { UploadArea } from 'src/bitable/table-view/cell/files';
import { removeUploadInfoByKey, useUploadInfoById } from 'src/services/upload';
import { Button } from 'src/common/components/button';
import { LoadingIcon } from 'src/common/components/loading-icon';
import { UploadStatus } from 'src/redux/types';
import { fileUploader } from '@flowus/upload';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { usePDFHistorySideBar } from 'src/services/app';
import { TogglePdfAISidebar } from './toggle-sidebar';

const pdfDescList = [
  {
    img: Images.pdfSummary,
    title: '文件摘要',
    desc: '智能识别关键信息，快速创建摘要、捕捉文档要点',
  },
  {
    img: Images.pdfAsk,
    title: '智能问答',
    desc: '根据文档内容回答问题，帮助你深入理解',
  },
  {
    img: Images.pdfCompare,
    title: '内容对照',
    desc: '支持引用并轻松跳转至原文，方便准确对照',
  },
];

export const PDFUploadArea: FC<{
  handleUpload: (files: FileList) => string | undefined;
  retry: () => void;
  blockId: string;
  setBlockId: React.Dispatch<React.SetStateAction<string>>;
}> = ({ retry, setBlockId, handleUpload, blockId }) => {
  const info = useUploadInfoById(blockId);
  const { enableAI } = useEnableAI();
  const pdfHistorySideBar = usePDFHistorySideBar();

  const cancelUploadFile = () => {
    setBlockId('');
    void fileUploader.abort(info?.uploadId ?? '');
    removeUploadInfoByKey(info?.key ?? '');
  };

  return (
    <div className="flex-1 flex flex-col w-full">
      <div className="h-[55px] flex p-4 items-center flex-shrink-0">
        {enableAI && !pdfHistorySideBar && <TogglePdfAISidebar />}
      </div>
      <div className="overflow-x-auto w-full flex-1">
        <div className="flex-1 border-t border-grey6 px-20 py-14 min-w-[600px]">
          {info && info.status !== UploadStatus.success && (
            <div className="border border-dashed rounded-md flex items-center justify-center flex-col py-[150px]">
              <Icon size="normal" name="MIcPdf" className="w-20 h-20" />
              <p className="text-t1 text-black mt-5">{info.name}</p>

              {info.status === UploadStatus.failure && (
                <>
                  <span className="text-t2-medium text-red">⚠️ 文件上传失败</span>
                  <div className="flex ">
                    <Button colorType="active" className="w-[86px] mt-5" onClick={retry}>
                      重试
                    </Button>
                    <Button
                      colorType="base_white"
                      className="border w-[86px] border-grey5 mt-5 ml-2.5"
                      onClick={cancelUploadFile}
                    >
                      取消
                    </Button>
                  </div>
                </>
              )}

              {info.status === UploadStatus.uploading && (
                <>
                  <div className="w-[300px] bg-grey9 rounded-sm h-[3px] mt-5">
                    <div
                      className="bg-active_color h-full"
                      style={{ width: `${info.progress}%` }}
                    />
                  </div>
                  <p className="text-t2 text-grey3 mt-4 flex items-center">
                    <LoadingIcon className="mr-1" size="middle" /> 正在上传...({info.progress}%)
                  </p>
                  <Button
                    colorType="base_white"
                    className="border w-[86px] border-grey5 mt-5"
                    onClick={cancelUploadFile}
                  >
                    取消
                  </Button>
                </>
              )}
            </div>
          )}

          {(!info || info.status === UploadStatus.success) && (
            <UploadArea
              accept="application/pdf"
              className="border border-dashed rounded-md flex items-center justify-center flex-col py-[150px] relative hover:border-active_color hover:bg-active_color/10"
              onSelect={handleUpload}
            >
              <img src={Images.pdfFile} alt="" className="w-20 h-20" />
              <p className="text-t1 text-black mt-5">点击或将文件拖放到此处上传</p>
              <p className="text-t2 text-grey3 mt-2.5">
                <span>文件类型：PDF</span>
                <span className="ml-5">最大文件页数：200页/文件</span>
              </p>
            </UploadArea>
          )}

          <div className="rounded-md border mt-10 justify-between p-5 grid grid-cols-3 gap-4">
            {pdfDescList.map((item) => {
              return (
                <div className="flex" key={item.title}>
                  <img src={item.img} alt="" className="w-14 h-14 rounded" />
                  <div className="ml-2 space-y-2">
                    <p className="text-t1 text-ellipsis">{item.title}</p>
                    <p className="text-grey3 line-clamp-2 text-t4">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
